import React, { ReactElement, useCallback, useState, WeakValidationMap } from 'react';
import PropTypes from 'prop-types';
import { forgeClassHelper } from '../utils/classes';
import CloseSmall from '@athena/forge-icons/dist/CloseSmall';

const classes = forgeClassHelper({ name: 'removable-tag' });

export type RemovableTagSize = 'medium' | 'small';
const sizes: RemovableTagSize[] = ['medium', 'small'];

export interface RemovableTagProps extends React.HTMLAttributes<HTMLSpanElement> {
  className?: string;
  disabled?: boolean;
  onRemove: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  size?: RemovableTagSize;
  tabIndex?: number;
  text: string;
}

function RemovableTag({
  className,
  disabled,
  onRemove,
  size = 'medium',
  tabIndex,
  text,
  ...rest
}: RemovableTagProps): ReactElement {
  const [active, setActive] = useState(false);

  const toggleActiveClass = useCallback(() => {
    setActive((currentActive) => !currentActive);
  }, []);

  const removeIconTitle = `Remove ${text}`;

  return (
    <span
      {...rest}
      {...classes({
        modifiers: { [size]: size !== 'medium' },
        states: { disabled: !!disabled },
        extra: className,
      })}
      onTouchStart={toggleActiveClass}
      onTouchEnd={() => {
        toggleActiveClass();
        onRemove();
      }}
      onTouchCancel={toggleActiveClass}
      title={text}
    >
      <button
        {...classes({ element: 'remove', states: { active, disabled: !!disabled } })}
        onClick={onRemove}
        tabIndex={tabIndex}
        type="button"
      >
        <CloseSmall {...classes({ element: 'close-icon' })} size="10px" title={removeIconTitle} />
      </button>
      {/** Text must be in the DOM after the button in order for the CSS
       * element+element Selector to recolor text based on button state.
       *
       * The `order` CSS property causes the button to render independent of
       * DOM order.
       */}
      <span {...classes({ element: 'text', states: { disabled: !!disabled } })}>{text}</span>
    </span>
  );
}

// propTypes comments are used to generate documentation and *must* be included
const removableTagPropTypes: WeakValidationMap<RemovableTagProps & { '...rest': unknown }> = {
  /** Handles click event on the remove element */
  onRemove: PropTypes.func.isRequired,
  /** The string displayed in the component */
  text: PropTypes.string.isRequired,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Adds a class that can be used for styling, and hides the remove button */
  disabled: PropTypes.bool,
  /** Applies a class to change the size of the component */
  size: PropTypes.oneOf(sizes),
  /** Sets the `tabindex` on the button element */
  tabIndex: PropTypes.number,
  /** Additional props are passed to the component's root element. */
  '...rest': PropTypes.any,
};

RemovableTag.propTypes = removableTagPropTypes;
RemovableTag.SIZES = sizes;

export default RemovableTag;
